import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isBranchEnv } from 'brand-config';

import { getBackgroundPreference, saveBackgroundPreference } from 'core/components/theme/background';
import { getCondensedLayoutPreference, saveCondensedLayoutPreference } from 'core/components/theme/condensed-layout';
import BaseDialog from 'core/components/base-dialog/base-dialog';
import Button from 'core/components/button/button';
import { Label } from 'core/components/label';
import Checkbox from 'core/components/checkbox';
import { trackAndWait } from 'core/helpers/analytics';
import { getThemeVariant, saveThemeVariant, THEME_VARIANTS } from 'core/components/theme/theme-variant';
import useStyles from './theme-switch-dialog.styles';
import {
  getMotionPreference,
  saveMotionPreference,
  getEnhancedLoadingPreference,
  saveEnhancedLoadingPreference
} from 'core/components/theme/motion';

export const themeSwitchLabels = {
  [THEME_VARIANTS.LEGACY]: 'Try the new look',
  [THEME_VARIANTS.RESKIN]: 'Switch to the classic look'
};

const RELOAD_CANCELLED_AFTER_MS = 1000;

const ThemeSwitchDialog = ({ open, onClose, customizeOnly = false }) => {
  const classes = useStyles();
  const [appliedBackground] = useState(getBackgroundPreference);
  const [background, setBackground] = useState(appliedBackground);
  const [appliedMotion] = useState(getMotionPreference);
  const [motionEnabled, setMotionEnabled] = useState(appliedMotion);
  const [appliedEnhancedLoading] = useState(getEnhancedLoadingPreference);
  const [enhancedLoadingEnabled, setEnhancedLoadingEnabled] = useState(appliedEnhancedLoading);
  const [appliedCondensedLayout] = useState(getCondensedLayoutPreference);
  const [condensedLayoutEnabled, setCondensedLayoutEnabled] = useState(appliedCondensedLayout);
  const [switching, setSwitching] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const switchingRef = useRef(false);
  const switchAttemptedRef = useRef(false);

  const themeVariant = getThemeVariant();
  const targetThemeVariant =
    customizeOnly || themeVariant === THEME_VARIANTS.LEGACY ? THEME_VARIANTS.RESKIN : THEME_VARIANTS.LEGACY;
  const label = customizeOnly ? 'Apply & refresh' : themeSwitchLabels[themeVariant];
  const choosingAppearance = targetThemeVariant === THEME_VARIANTS.RESKIN;

  const switchThemeVariant = async () => {
    if (switchingRef.current) {
      return;
    }
    if (!saveThemeVariant(targetThemeVariant)) {
      setSaveFailed(true);
      return;
    }
    if (choosingAppearance && !saveMotionPreference(motionEnabled)) {
      saveThemeVariant(themeVariant);
      setSaveFailed(true);
      return;
    }
    if (choosingAppearance && !saveEnhancedLoadingPreference(enhancedLoadingEnabled)) {
      saveThemeVariant(themeVariant);
      saveMotionPreference(appliedMotion);
      setSaveFailed(true);
      return;
    }
    if (choosingAppearance && !saveBackgroundPreference(background)) {
      saveThemeVariant(themeVariant);
      saveMotionPreference(appliedMotion);
      saveEnhancedLoadingPreference(appliedEnhancedLoading);
      setSaveFailed(true);
      return;
    }
    if (choosingAppearance && !saveCondensedLayoutPreference(condensedLayoutEnabled)) {
      saveThemeVariant(themeVariant);
      saveMotionPreference(appliedMotion);
      saveEnhancedLoadingPreference(appliedEnhancedLoading);
      saveBackgroundPreference(appliedBackground);
      setSaveFailed(true);
      return;
    }
    switchingRef.current = true;
    switchAttemptedRef.current = true;
    setSwitching(true);
    try {
      if (targetThemeVariant !== themeVariant) {
        await trackAndWait('Staff Theme Changed', { fromVariant: themeVariant, toVariant: targetThemeVariant });
      }
    } catch {
      // Tracking is best-effort; switch anyway.
    }
    // reload() returns once a leave-page prompt is answered; if the page is still here after that, the user
    // chose to stay, so make the dialog usable again.
    window.location.reload();
    setTimeout(() => {
      switchingRef.current = false;
      setSwitching(false);
    }, RELOAD_CANCELLED_AFTER_MS);
  };

  const close = () => {
    // Backing out keeps the current look on the next page load.
    if (switchAttemptedRef.current) {
      saveThemeVariant(themeVariant);
      if (choosingAppearance) {
        saveMotionPreference(appliedMotion);
        saveEnhancedLoadingPreference(appliedEnhancedLoading);
        saveBackgroundPreference(appliedBackground);
        saveCondensedLayoutPreference(appliedCondensedLayout);
      }
      switchAttemptedRef.current = false;
    }
    setSaveFailed(false);
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={switching ? undefined : close}
      size="sm"
      className={classes.dialog}
      aria-labelledby="theme-switch-title"
    >
      <div className={classes.content}>
        <Label id="theme-switch-title" type="greenBig" className={classes.title}>
          {customizeOnly ? 'Customize appearance' : `${label}?`}
        </Label>
        {choosingAppearance && (
          <fieldset className={classes.appearanceOptions} disabled={switching}>
            <Checkbox
              id="staff-condensed-layout-enabled"
              label="Condensed layout"
              mode="light"
              value={condensedLayoutEnabled}
              disabled={switching}
              onChange={(event) => setCondensedLayoutEnabled(event.target.checked)}
            />
            <Label type="darkGreenMedium" className={classes.customHint}>
              Uses your screen’s full width and moves navigation to a menu on the left.
            </Label>
            <Checkbox
              id="staff-motion-enabled"
              label="Enable motion"
              mode="light"
              value={motionEnabled}
              disabled={switching}
              onChange={(event) => setMotionEnabled(event.target.checked)}
            />
            <Label type="darkGreenMedium" className={classes.customHint}>
              Adds button feedback and animated section changes. Your device’s reduced motion setting takes priority.
            </Label>
            <Checkbox
              id="staff-enhanced-loading-enabled"
              label="Enhanced loading animations"
              mode="light"
              value={enhancedLoadingEnabled}
              disabled={switching}
              onChange={(event) => setEnhancedLoadingEnabled(event.target.checked)}
            />
            <Label type="darkGreenMedium" className={classes.customHint}>
              Shows full-page illustrations while creating or updating offers and previewing policy changes. Your
              device’s reduced motion setting takes priority.
            </Label>
          </fieldset>
        )}
        {choosingAppearance && isBranchEnv && (
          <fieldset className={classes.appearanceOptions} disabled={switching}>
            <legend className={classes.legend}>Background</legend>
            <Checkbox
              id="staff-background-artwork"
              label="Gutter illustrations"
              mode="light"
              value={background.gutterArtwork}
              onChange={(event) => setBackground({ ...background, gutterArtwork: event.target.checked })}
            />
            <Label type="darkGreenMedium" className={classes.customHint}>
              Adds Branch artwork in the side margins when there’s room.
            </Label>
          </fieldset>
        )}
        <Label type="darkGreenMedium" className={classes.message}>
          The page will reload, and any unsaved changes will be lost. You can switch back anytime from this menu.
        </Label>
        {saveFailed && (
          <Label type="darkGreenMedium" className={classes.error} role="alert">
            Your browser blocked saving this setting, so the look didn't change. Allow this site to store data, then try
            again.
          </Label>
        )}
        <Button
          className={classes.confirmButton}
          variant="contained"
          color="primary"
          onClick={switchThemeVariant}
          loading={switching}
          disabled={switching}
        >
          {label}
        </Button>
        <Button variant="text" color="secondary" onClick={close} disabled={switching}>
          Go back
        </Button>
      </div>
    </BaseDialog>
  );
};

ThemeSwitchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customizeOnly: PropTypes.bool
};

export default ThemeSwitchDialog;
